import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../index';
import { Button } from './Button';

export interface PaginationProps {
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  hasPrev: boolean;
  hasNext: boolean;
  onPageChange: (page: number) => void;
  /** Optional page-size selector */
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
  /** Optional noun for the count label, e.g. "contacts" */
  itemLabel?: string;
  className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  page, totalPages, total, limit, hasPrev, hasNext,
  onPageChange, pageSizeOptions, onPageSizeChange, itemLabel, className,
}) => {
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  const showSize = pageSizeOptions && onPageSizeChange;

  // Hide entirely only when there's nothing to page and no size control.
  if (totalPages <= 1 && !showSize) return null;

  return (
    <div className={cn('flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between px-3 py-2.5 border-t border-[var(--border)]', className)}>
      <div className="flex items-center gap-3">
        <p className="text-xs text-[var(--fg-muted)]">
          Showing {from} to {to} of {total.toLocaleString()}
          {itemLabel ? ` ${itemLabel}` : ''}
        </p>
        {showSize && (
          <label className="flex items-center gap-1.5 text-xs text-[var(--fg-muted)]">
            <span className="hidden sm:inline">Rows per page</span>
            <select
              value={limit}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="h-7 rounded-md border border-[var(--input-border)] bg-[var(--input)] px-1.5 text-xs text-[var(--fg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
              aria-label="Rows per page"
            >
              {pageSizeOptions!.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center gap-1.5">
          <Button variant="secondary" size="sm" disabled={!hasPrev} onClick={() => onPageChange(page - 1)} aria-label="Previous page">
            <ChevronLeft className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Prev</span>
          </Button>
          <span className="text-xs text-[var(--fg-muted)] px-1.5 tabular-nums">
            {page} / {totalPages}
          </span>
          <Button variant="secondary" size="sm" disabled={!hasNext} onClick={() => onPageChange(page + 1)} aria-label="Next page">
            <span className="hidden sm:inline">Next</span>
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
};
